import { useState } from "react";
import { useTranslation } from "react-i18next";

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { type RoadmapTriageEntry, useDeleteRoadmapTriage } from "@/lib/api";
import { apiErrorMessage, inlineFailure } from "@/lib/api-errors";
import type { NamedPerson } from "@/lib/people";

import { StepButtons } from "./step-buttons";
import { submitterLabel, triageKind } from "./triage";
import { TriageEntryTitle, useTriageReturnFocus } from "./triage-dialog-parts";

interface DeleteTriageDialogProps {
  /** The entry to delete; it stays while the dialog closes. */
  entry: RoadmapTriageEntry | null;
  /**
   * The entry has left the triage since the page loaded (`triageEntryGone`):
   * another developer, or Claude, handled it. The dialog says so and deletes nothing.
   */
  entryGone?: boolean;
  /** The people the triage names its submitters among (`triagePeople`), by email in lower case. */
  people: ReadonlyMap<string, NamedPerson>;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

/**
 * Verwijderen on a triage entry, asked first in red: the entry is deleted
 * for good with the text its submitter typed, which cannot be undone, while
 * Sluiten keeps it. The entry's title, then Annuleren, which takes the focus
 * as the safe choice, or Verwijderen. Should the entry leave the triage while
 * the dialog is open, it says so and Verwijderen stays off. The focus goes
 * back as after Sluiten (`useTriageReturnFocus`).
 */
export function DeleteTriageDialog({
  entry,
  entryGone = false,
  people,
  open,
  onOpenChange,
}: DeleteTriageDialogProps) {
  const { t } = useTranslation();
  const onCloseAutoFocus = useTriageReturnFocus(open, entry);
  const kind = entry ? triageKind(entry) : "idea";
  return (
    <Dialog open={open && entry !== null} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md" onCloseAutoFocus={onCloseAutoFocus}>
        <DialogHeader>
          <DialogTitle>{t(`roadmap.triage.deleteTitle.${kind}`)}</DialogTitle>
          <DialogDescription>
            {t(`roadmap.triage.deleteDescription.${kind}`, {
              person: entry ? submitterLabel(entry, people) : "",
            })}
          </DialogDescription>
        </DialogHeader>
        {/* Mounted while open only, so every opening starts without an earlier failure. */}
        {open && entry && (
          <DeleteTriageStep
            key={entry.id}
            entry={entry}
            entryGone={entryGone}
            onDone={() => onOpenChange(false)}
          />
        )}
      </DialogContent>
    </Dialog>
  );
}

interface DeleteTriageStepProps {
  entry: RoadmapTriageEntry;
  /** The entry has left the triage meanwhile: handled elsewhere. */
  entryGone: boolean;
  /** Back to the table: after the entry is gone, or on Annuleren. */
  onDone: () => void;
}

/** The question's body: the entry's title, the failure of the last attempt and the buttons. */
export function DeleteTriageStep({ entry, entryGone, onDone }: DeleteTriageStepProps) {
  const { t } = useTranslation();
  const remove = useDeleteRoadmapTriage();
  // The failed attempt, worded at render so a language switch re-words it.
  const [failure, setFailure] = useState<{ error: unknown } | null>(null);
  // Left the triage, and not through this step's own delete (which ends the dialog once the roadmap shows it).
  const handled = entryGone && (remove.isIdle || remove.isError);

  const submit = async () => {
    if (remove.isPending || handled) {
      return;
    }
    setFailure(null);
    try {
      await remove.mutateAsync({ entryId: entry.id });
      onDone();
    } catch (error) {
      setFailure(inlineFailure({ error }));
    }
  };

  return (
    <div className="space-y-4">
      <TriageEntryTitle entry={entry} />
      {handled ? (
        <p role="alert" className="text-destructive text-sm">
          {t("errors.codes.roadmap_triage_not_found")}
        </p>
      ) : (
        failure && (
          <p role="alert" className="text-destructive text-sm">
            {apiErrorMessage(failure.error, "deleteRoadmapTriage")}
          </p>
        )
      )}
      <StepButtons
        onBack={onDone}
        onConfirm={() => void submit()}
        confirm={t("roadmap.triage.delete")}
        pending={remove.isPending}
        disabled={handled}
        destructive
        autoFocusBack
      />
    </div>
  );
}
